import * as ngCore from '@angular/core'

import type * as models from '../../../models'

/**
 * Owns the lifecycle of a configured custom cell component. A new renderer
 * snapshot replaces the previous component and all of its typed bindings.
 */
@ngCore.Component({
  selector: 'atn-ds-table-cell-outlet',
  styleUrl: './component.scss',
  template: '',
})
export class Component {
  /** Dynamic component and bindings for the current cell. */
  public readonly rendererSg = ngCore.input.required<models.Renderer.Component>(
    { alias: 'renderer' },
  )

  /** Inline styles applied to the configured component's host element. */
  public readonly cssSg = ngCore.input<models.Styles | undefined>(undefined, {
    alias: 'css',
  })

  /** Host container in which the configured component is created. */
  private readonly _container = ngCore.inject(ngCore.ViewContainerRef)

  /** Recreates the custom component when its immutable renderer changes. */
  constructor() {
    ngCore.effect(() => {
      const renderer = this.rendererSg()
      const css = this.cssSg()
      // Bindings are created from the immutable renderer snapshot. Replacing
      // the snapshot replaces the component and avoids carrying inputs or
      // outputs from a previous renderer into the new cell.
      const inputBindings = Object.entries(renderer.inputs ?? {}).map(
        ([name, inputValue]) => ngCore.inputBinding(name, () => inputValue),
      )
      const outputBindings = Object.entries(renderer.outputs ?? {}).flatMap(
        ([name, callback]) =>
          callback ? [ngCore.outputBinding(name, callback)] : [],
      )

      // A cell has one active dynamic renderer. Clear it before creation so
      // repeated row updates cannot leave stale component instances in the
      // outlet container.
      this._container.clear()
      const componentRef = this._container.createComponent(renderer.component, {
        bindings: [...inputBindings, ...outputBindings],
      })
      if (css) {
        const element = componentRef.location.nativeElement as HTMLElement

        Object.assign(element.style, css)
      }
    })
  }
}
